import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { ArrowLeft, Save, Plus, Trash2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminPricing = () => {
  const navigate = useNavigate();
  const [pricingCategories, setPricingCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchPricing();
  }, []);

  const fetchPricing = async () => {
    try {
      const response = await axios.get(`${API}/pricing`);
      setPricingCategories(response.data);
    } catch (error) {
      console.error('Error fetching pricing:', error);
    }
  };

  const handleUpdate = async (id, updates) => {
    try {
      await axios.put(`${API}/pricing/${id}`, updates);
      fetchPricing();
      toast.success('Pricing updated!');
    } catch (error) {
      toast.error('Failed to update pricing');
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this pricing category?')) return;
    
    try {
      await axios.delete(`${API}/pricing/${id}`);
      fetchPricing();
      toast.success('Pricing deleted!');
    } catch (error) {
      toast.error('Failed to delete pricing');
      console.error(error);
    }
  };

  const handleCategoryChange = (id, value) => {
    setPricingCategories(pricingCategories.map(p => 
      p.id === id ? { ...p, category: value } : p
    ));
  };

  const handleSizeChange = (categoryId, sizeIndex, field, value) => {
    setPricingCategories(pricingCategories.map(p => {
      if (p.id === categoryId) {
        const newSizes = [...p.sizes];
        newSizes[sizeIndex] = { ...newSizes[sizeIndex], [field]: value };
        return { ...p, sizes: newSizes };
      }
      return p;
    }));
  };

  const addSize = (categoryId) => {
    setPricingCategories(pricingCategories.map(p => {
      if (p.id === categoryId) {
        return {
          ...p,
          sizes: [...p.sizes, { size: '', price: '', duration: '' }]
        };
      }
      return p;
    }));
  };

  const removeSize = (categoryId, sizeIndex) => {
    setPricingCategories(pricingCategories.map(p => {
      if (p.id === categoryId) {
        const newSizes = p.sizes.filter((_, idx) => idx !== sizeIndex);
        return { ...p, sizes: newSizes };
      }
      return p;
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center space-x-4">
            <Button variant="ghost" onClick={() => navigate('/admin/dashboard')}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
            <h1 className="text-2xl font-bold text-gray-900">Manage Pricing</h1>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="space-y-6">
          {pricingCategories.map((category) => (
            <Card key={category.id}>
              <CardHeader className="bg-amber-50">
                <div className="flex items-center justify-between">
                  <Input
                    value={category.category}
                    onChange={(e) => handleCategoryChange(category.id, e.target.value)}
                    onBlur={() => handleUpdate(category.id, { category: category.category })}
                    className="text-xl font-bold max-w-md"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDelete(category.id)}
                    className="border-red-500 text-red-500 hover:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4 mr-1" />
                    Delete Category
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-6 space-y-4">
                {category.sizes.map((size, idx) => (
                  <div key={idx} className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end p-4 bg-gray-50 rounded-lg">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Size/Type
                      </label>
                      <Input
                        value={size.size}
                        onChange={(e) => handleSizeChange(category.id, idx, 'size', e.target.value)}
                        onBlur={() => handleUpdate(category.id, { sizes: category.sizes })}
                        placeholder="e.g., Small, Medium"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Price (CHF)
                      </label>
                      <Input
                        value={size.price}
                        onChange={(e) => handleSizeChange(category.id, idx, 'price', e.target.value)}
                        onBlur={() => handleUpdate(category.id, { sizes: category.sizes })}
                        placeholder="e.g., CHF 200-250"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Duration
                      </label>
                      <Input
                        value={size.duration}
                        onChange={(e) => handleSizeChange(category.id, idx, 'duration', e.target.value)}
                        onBlur={() => handleUpdate(category.id, { sizes: category.sizes })}
                        placeholder="e.g., 4-5 hours"
                      />
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => removeSize(category.id, idx)}
                      className="border-red-300 text-red-600 hover:bg-red-50"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                
                <Button
                  variant="outline"
                  onClick={() => addSize(category.id)}
                  className="w-full border-dashed"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Size Option
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        <Card className="mt-8 bg-amber-50 border-amber-200">
          <CardContent className="p-6">
            <p className="text-gray-700">
              💡 <strong>Tip:</strong> Click on any field to edit pricing. Changes save automatically when you click outside the field. 
              Add or remove size options for each service category.
            </p>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AdminPricing;
